import { isTagManifest, type TagManifest } from "@customyai/site";
export { isTagManifest, type TagManifest };

/** The manifest the site serves when tags are off or Customy is unreachable. */
export const EMPTY_TAG_MANIFEST: TagManifest = { schemaVersion: 1, revision: "empty", generatedAt: "1970-01-01T00:00:00.000Z", sourceId: "", tags: [], csp: { script: [], connect: [], img: [], frame: [] } };

/** Browser identifiers that let Customy Data deduplicate its server-side forwarding (GA4, Meta) against the tags in the page.
 * Strict shapes only: this crosses the first-party analytics boundary, which otherwise carries no identifiers. */
export type AnalyticsIdentifiers = { gaClientId?: string; gaSessionId?: string; fbp?: string; fbc?: string; ttclid?: string; ttp?: string; liFatId?: string; gclid?: string };
const identifierShapes: Record<keyof AnalyticsIdentifiers, RegExp> = {
  gaClientId: /^[0-9]{6,12}\.[0-9]{6,12}$/, gaSessionId: /^[0-9]{6,12}$/, fbp: /^fb\.1\.[0-9]{1,16}\.[0-9]{1,20}$/, fbc: /^fb\.1\.[0-9]{1,16}\.[A-Za-z0-9_-]{1,200}$/,
  // Click ids for TikTok Events API, LinkedIn CAPI and Google Ads: opaque tokens, bounded and URL-safe.
  ttclid: /^[A-Za-z0-9_.-]{8,500}$/, ttp: /^[A-Za-z0-9_.-]{8,200}$/, liFatId: /^[A-Za-z0-9_.-]{8,200}$/, gclid: /^[A-Za-z0-9_-]{8,200}$/,
};
export function parseAnalyticsIdentifiers(value: unknown): AnalyticsIdentifiers | null {
  if (value === undefined) return null;
  if (!value || typeof value !== "object" || Array.isArray(value)) throw new Error("ANALYTICS_INPUT_INVALID");
  const out: AnalyticsIdentifiers = {};
  for (const [key, raw] of Object.entries(value as Record<string, unknown>)) {
    const shape = identifierShapes[key as keyof AnalyticsIdentifiers];
    if (!shape || typeof raw !== "string" || !shape.test(raw)) throw new Error("ANALYTICS_INPUT_INVALID");
    out[key as keyof AnalyticsIdentifiers] = raw;
  }
  return Object.keys(out).length ? out : null;
}

/** The `context` fragment Customy Data reads for server-side forwarding. */
export function identifiersContext(identifiers: AnalyticsIdentifiers | null | undefined) {
  if (!identifiers) return {};
  return {
    ...(identifiers.gaClientId ? { ga: { clientId: identifiers.gaClientId, ...(identifiers.gaSessionId ? { sessionId: identifiers.gaSessionId } : {}) } } : {}),
    ...(identifiers.fbp || identifiers.fbc ? { meta: { ...(identifiers.fbp ? { fbp: identifiers.fbp } : {}), ...(identifiers.fbc ? { fbc: identifiers.fbc } : {}) } } : {}),
    ...(identifiers.ttclid || identifiers.ttp ? { tiktok: { ...(identifiers.ttclid ? { ttclid: identifiers.ttclid } : {}), ...(identifiers.ttp ? { ttp: identifiers.ttp } : {}) } } : {}),
    ...(identifiers.liFatId ? { linkedin: { liFatId: identifiers.liFatId } } : {}),
    ...(identifiers.gclid ? { google: { gclid: identifiers.gclid } } : {}),
  };
}
